import { useAdmin, resolveImgUrl } from "./AdminContext";
import { Plus, Pencil, Trash2, Package, Minus, Copy, Images } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export function AdminInventory() {
  const { products, productsLoading, handleNewProduct, handleEditProduct, handleDeleteProduct, handleDuplicateProduct, adjustQuantity, openImagesDialog } = useAdmin();

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-2xl font-black uppercase">Products</h2>
          <p className="text-sm text-zinc-500 mt-1">{products.length} products in inventory</p>
        </div>
        <button onClick={handleNewProduct} className="brutal-btn text-sm flex items-center gap-2" data-testid="add-product-btn">
          <Plus size={16} strokeWidth={2.5} /> Add Product
        </button>
      </div>
      {productsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{[1, 2, 3].map(i => <div key={i} className="brutal-card h-48 animate-pulse" />)}</div>
      ) : products.length === 0 ? (
        <div className="brutal-card p-12 text-center" data-testid="no-products">
          <Package size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={2.5} />
          <h3 className="font-heading text-lg font-bold mb-2">No Products</h3>
          <p className="text-zinc-500 text-sm mb-6">Add your first product to get started.</p>
          <button onClick={handleNewProduct} className="brutal-btn text-sm" data-testid="add-first-product-btn"><Plus size={14} className="inline mr-2" strokeWidth={2.5} /> Add Product</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-products-grid">
          {products.map(product => (
            <div key={product.id} className="brutal-card" data-testid={`admin-product-${product.id}`}>
              <div className="h-36 bg-zinc-100 border-b-2 border-black relative">
                {product.image_url ? (
                  <img src={resolveImgUrl(product.image_url)} alt={product.name} className="w-full h-full object-contain p-2" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Package size={32} className="text-zinc-300" strokeWidth={2.5} /></div>
                )}
                {!product.active && <Badge className="absolute top-2 right-2 bg-zinc-800 text-white border-0 rounded-none font-mono text-[10px]">Inactive</Badge>}
                {(product.quantity || 0) <= 0 && <Badge className="absolute top-2 left-2 bg-red-500 text-white border-0 rounded-none font-mono text-[10px]">Out of Stock</Badge>}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h4 className="font-heading font-bold text-sm uppercase">{product.name}</h4>
                    {product.category && <span className="text-xs text-zinc-500">{product.category}</span>}
                  </div>
                  <span className="font-heading font-black text-lg text-[#FF3366]">${product.price?.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-3 mt-3 mb-3" data-testid={`quantity-controls-${product.id}`}>
                  <span className="font-heading font-bold text-xs uppercase text-zinc-500">Qty:</span>
                  <button onClick={() => adjustQuantity(product.id, -1)} className="w-7 h-7 border-2 border-black bg-white flex items-center justify-center hover:bg-zinc-100 transition-colors" data-testid={`qty-minus-${product.id}`}><Minus size={12} strokeWidth={3} /></button>
                  <span className={`font-heading font-black text-lg min-w-[2rem] text-center ${(product.quantity || 0) <= 0 ? "text-red-500" : ""}`} data-testid={`qty-value-${product.id}`}>{product.quantity || 0}</span>
                  <button onClick={() => adjustQuantity(product.id, 1)} className="w-7 h-7 border-2 border-black bg-white flex items-center justify-center hover:bg-zinc-100 transition-colors" data-testid={`qty-plus-${product.id}`}><Plus size={12} strokeWidth={3} /></button>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => openImagesDialog(product.id)} className="brutal-btn brutal-btn-tertiary text-xs px-3 py-2 flex items-center gap-1" data-testid={`manage-images-${product.id}`}><Images size={12} strokeWidth={2.5} /> Images</button>
                  <button onClick={() => handleEditProduct(product)} className="brutal-btn brutal-btn-secondary text-xs px-3 py-2 flex items-center gap-1" data-testid={`edit-product-${product.id}`}><Pencil size={12} strokeWidth={2.5} /> Edit</button>
                  <button onClick={() => handleDuplicateProduct(product.id)} className="brutal-btn brutal-btn-outline text-xs px-3 py-2 flex items-center gap-1" data-testid={`duplicate-product-${product.id}`}><Copy size={12} strokeWidth={2.5} /> Duplicate</button>
                  <button onClick={() => handleDeleteProduct(product.id)} className="brutal-btn brutal-btn-outline text-xs px-3 py-2 flex items-center gap-1 border-red-500 text-red-500" data-testid={`delete-product-${product.id}`}><Trash2 size={12} strokeWidth={2.5} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminEngraving() {
  const { categories, categoriesLoading, handleNewCategory, handleEditCategory, handleDeleteCategory } = useAdmin();

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-2xl font-black uppercase">Engraving Categories</h2>
          <p className="text-sm text-zinc-500 mt-1">Set pricing for different engraving types</p>
        </div>
        <button onClick={handleNewCategory} className="brutal-btn text-sm flex items-center gap-2" data-testid="add-category-btn"><Plus size={16} strokeWidth={2.5} /> Add Category</button>
      </div>
      {categoriesLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{[1, 2].map(i => <div key={i} className="brutal-card h-32 animate-pulse" />)}</div>
      ) : categories.length === 0 ? (
        <div className="brutal-card p-12 text-center" data-testid="no-categories">
          <h3 className="font-heading text-lg font-bold mb-2">No Engraving Categories</h3>
          <button onClick={handleNewCategory} className="brutal-btn text-sm" data-testid="add-first-category-btn"><Plus size={14} className="inline mr-2" strokeWidth={2.5} /> Add Category</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="categories-grid">
          {categories.map(cat => (
            <div key={cat.id} className="brutal-card p-6" data-testid={`category-${cat.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div><h4 className="font-heading font-bold text-sm uppercase">{cat.name}</h4>{cat.description && <p className="text-xs text-zinc-500 mt-1">{cat.description}</p>}{cat.style_type && <span className="inline-block bg-zinc-100 text-zinc-600 text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 mt-1 border border-zinc-300">{cat.style_type.replace(/_/g, " ")}</span>}</div>
                <span className={`font-heading font-black text-xl ${cat.active ? "text-[#FF3366]" : "text-zinc-400"}`}>${cat.price?.toFixed(2)}</span>
              </div>
              {!cat.active && <span className="inline-block bg-zinc-200 text-zinc-600 text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-1 mb-3">Inactive</span>}
              <div className="flex gap-2">
                <button onClick={() => handleEditCategory(cat)} className="brutal-btn brutal-btn-secondary text-xs px-3 py-2 flex items-center gap-1" data-testid={`edit-category-${cat.id}`}><Pencil size={12} strokeWidth={2.5} /> Edit</button>
                <button onClick={() => handleDeleteCategory(cat.id)} className="brutal-btn brutal-btn-outline text-xs px-3 py-2 flex items-center gap-1 border-red-500 text-red-500" data-testid={`delete-category-${cat.id}`}><Trash2 size={12} strokeWidth={2.5} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
